import { createAccountMenuLoginView } from "../account/account-menu-login-view.js";
import { accountFlowUrls } from "../account/account-route-contract.js";
import { createKratosBrowserClient } from "../account/kratos-browser-client.js";
import { identityBrowserContext } from "../account/kratos-browser-contract.js";
import {
  browserKratosIdentitySessionRepository,
  refreshBrowserKratosIdentitySessionRepository,
} from "../account/kratos-identity-session-repository.js";
import {
  identityProviderOutcomePresentation,
  startIdentityProviderPopup,
} from "../account/identity-provider-popup.js";
import { identityFormSubmission } from "../account/identity-form-submission.js";
import {
  webauthnCeremonyFailureCopy,
  performWebAuthnCeremony,
} from "../account/webauthn-ceremony.js";
import { translate } from "../account/identity-view-primitives.js";
import { isDiscordAvatarReference } from "../account/avatar-reference.js";
import {
  browserPrivacyControlsRepository,
  discordImageConsentDecision,
} from "../privacy-controls.js";

export const ACCOUNT_MENU_TAG = "fishy-account-menu";

const MARKERS = Object.freeze({
  loading: "[data-account-menu-loading]",
  signedOut: "[data-account-menu-signed-out]",
  loginPanel: "[data-account-menu-login-panel]",
  loginMount: "[data-account-menu-login-mount]",
  signedIn: "[data-account-menu-signed-in]",
  unavailable: "[data-account-menu-unavailable]",
  displayName: "[data-account-menu-display-name]",
  handle: "[data-account-menu-handle]",
  avatar: "[data-account-menu-avatar]",
  triggerAvatar: "[data-account-menu-trigger-avatar]",
  login: "[data-account-menu-login]",
  discord: "[data-account-menu-discord]",
  registration: "[data-account-menu-registration]",
  signOut: "[data-account-menu-sign-out]",
  recoverySignOut: "[data-account-menu-recovery-sign-out]",
});

function accountUrl(locationLike) {
  return String(locationLike?.pathname ?? "").startsWith("/de-DE/")
    ? `${locationLike.origin}/de-DE/konto/`
    : `${locationLike.origin}/account/`;
}

const HTMLElementBase = globalThis.HTMLElement ?? class {};

export class FishyAccountMenu extends HTMLElementBase {
  constructor() {
    super();
    this._connected = false;
    this._connectionRevision = 0;
    this._actionAbortController = null;
    this._passkeyAbortController = null;
    this._unsubscribe = null;
    this._privacyUnsubscribe = null;
    this._accountId = null;
    this._session = null;
    this._privacyState = Object.freeze({ kind: "uninitialized" });
    this._context = null;
    this._client = null;
    this._loginFlow = null;
    this._repositoryProvider = browserKratosIdentitySessionRepository;
    this._privacyRepositoryProvider = browserPrivacyControlsRepository;
    this._clientFactory = createKratosBrowserClient;
    this._providerPopup = startIdentityProviderPopup;
    this._passkeyCeremony = performWebAuthnCeremony;
    this._refreshRepository = refreshBrowserKratosIdentitySessionRepository;
    this._onClick = this._onClick.bind(this);
    this._onSubmit = this._onSubmit.bind(this);
  }

  connectedCallback() {
    this._connected = true;
    this._connectionRevision += 1;
    const connectionRevision = this._connectionRevision;
    this.addEventListener("click", this._onClick);
    this.addEventListener("submit", this._onSubmit);
    this._renderMode("loading");

    try {
      this._context = identityBrowserContext({
        apiBaseUrl: globalThis.window?.__fishystuffApiBaseUrl,
        identityBaseUrl: globalThis.window?.__fishystuffRuntimeConfig?.identity?.baseUrl,
        locationLike: globalThis.window?.location,
        passkeyRpId: globalThis.window?.__fishystuffRuntimeConfig?.identity?.passkeyRpId,
        returnPath: new URL(accountUrl(globalThis.window?.location)).pathname,
      });
      this._client = this._clientFactory({ context: this._context });
      this._setActionLinks();
      const repository = this._repositoryProvider();
      this._unsubscribe = repository.subscribe((state) => {
        if (this._connected && this._connectionRevision === connectionRevision) {
          this._renderAccountState(state);
        }
      });
      Promise.resolve(repository.load()).catch(() => {
        if (this._connected && this._connectionRevision === connectionRevision) {
          this._renderMode("unavailable");
        }
      });
      const privacyRepository = this._privacyRepositoryProvider();
      this._privacyUnsubscribe = privacyRepository.subscribe((state) => {
        this._privacyState = state;
        if (this._session) {
          this._projectAvatar(
            this._session.traits.avatarUrl,
            this._session.traits.avatarColor,
          );
        }
      });
      Promise.resolve(privacyRepository.load()).catch(() => {
        this._privacyState = Object.freeze({ kind: "unavailable" });
        if (this._session) {
          this._projectAvatar(
            this._session.traits.avatarUrl,
            this._session.traits.avatarColor,
          );
        }
      });
    } catch (_error) {
      this._unsubscribe?.();
      this._unsubscribe = null;
      this._renderMode("unavailable");
    }
  }

  disconnectedCallback() {
    this._connected = false;
    this._connectionRevision += 1;
    this.removeEventListener("click", this._onClick);
    this.removeEventListener("submit", this._onSubmit);
    this._unsubscribe?.();
    this._unsubscribe = null;
    this._privacyUnsubscribe?.();
    this._privacyUnsubscribe = null;
    this._actionAbortController?.abort();
    this._actionAbortController = null;
    this._passkeyAbortController?.abort();
    this._passkeyAbortController = null;
  }

  _marker(selector) {
    return this.querySelector(selector);
  }

  _setActionLinks() {
    const urls = accountFlowUrls(
      globalThis.window?.location?.origin,
      new URL(accountUrl(globalThis.window?.location)).pathname,
    );
    this._marker(MARKERS.registration)?.setAttribute("href", urls.registration);
  }

  _setAvatar(value, {
    color = "base",
    loading = false,
    externalImagesAllowed = false,
  } = {}) {
    const avatar = this._marker(MARKERS.avatar);
    const trigger = this._marker(MARKERS.triggerAvatar);
    for (const marker of [avatar, trigger]) {
      if (!marker) continue;
      marker.toggleAttribute?.("loading", loading);
      marker.externalImagesAllowed = externalImagesAllowed;
      marker.color = color;
      marker.value = value;
    }
  }

  _renderMode(mode) {
    const modes = {
      loading: MARKERS.loading,
      signedOut: MARKERS.signedOut,
      login: MARKERS.loginPanel,
      signedIn: MARKERS.signedIn,
      unavailable: MARKERS.unavailable,
    };
    for (const [name, selector] of Object.entries(modes)) {
      const marker = this._marker(selector);
      if (marker) marker.hidden = name !== mode;
    }
    const recoverySignOut = this._marker(MARKERS.recoverySignOut);
    if (recoverySignOut) {
      recoverySignOut.hidden = mode !== "unavailable";
    }
    this.setAttribute?.("data-account-menu-state", mode);
    this.setAttribute?.("aria-busy", String(mode === "loading"));
  }

  _renderAccountState(state) {
    switch (state?.kind) {
      case "uninitialized":
      case "loading":
        this._setAvatar("", { loading: true });
        this._renderMode("loading");
        return;
      case "signedOut":
        this._accountId = null;
        this._session = null;
        this._loginFlow = null;
        this._setAvatar("");
        this._renderMode("signedOut");
        return;
      case "ready":
        this._accountId = state.accountId;
        this._session = state.session;
        this._projectSession(state.session);
        return;
      case "unavailable":
        this._accountId = state.accountId ?? null;
        this._session = null;
        this._setAvatar("", { loading: true });
        this._renderMode("unavailable");
        return;
      default:
        this._accountId = null;
        this._session = null;
        this._setAvatar("");
        this._renderMode("unavailable");
    }
  }

  _projectSession(session) {
    this._marker(MARKERS.displayName).textContent =
      session.traits.displayName || session.traits.username;
    this._marker(MARKERS.handle).textContent = session.traits.username;
    this._projectAvatar(session.traits.avatarUrl, session.traits.avatarColor);
    this._renderMode("signedIn");
    for (const warning of session.warnings ?? []) {
      globalThis.window?.__fishystuffToast?.warning?.(warning.message, {
        duration: Infinity,
      });
    }
  }

  _projectAvatar(value, color) {
    const decision = discordImageConsentDecision(this._privacyState);
    const waitingForConsent = isDiscordAvatarReference(value)
      && ["uninitialized", "loading"].includes(decision.repositoryKind);
    this._setAvatar(value, {
      color,
      loading: waitingForConsent,
      externalImagesAllowed: decision.effectiveEnabled,
    });
  }

  _setActionPending(pending) {
    for (const selector of [
      MARKERS.discord,
      MARKERS.signOut,
      MARKERS.recoverySignOut,
      MARKERS.login,
    ]) {
      const marker = this._marker(selector);
      if (marker) marker.disabled = pending;
    }
  }

  _toastError(message) {
    globalThis.window?.__fishystuffToast?.error?.(message, { duration: Infinity });
  }

  async _runAction(action) {
    this._actionAbortController?.abort();
    const controller = new AbortController();
    this._actionAbortController = controller;
    this._setActionPending(true);
    try {
      await action(controller.signal);
    } catch (error) {
      if (error?.name !== "AbortError" && this._connected) {
        const message = ["blocked", "expired"].includes(error?.kind)
          ? error.message
          : translate(
              "profile.menu.action_failed",
              "That account action could not be completed. Try again.",
            );
        this._toastError(message);
      }
    } finally {
      if (this._actionAbortController === controller) {
        this._actionAbortController = null;
        this._setActionPending(false);
      }
    }
  }

  _renderLoginFlow(flow) {
    this._loginFlow = flow;
    const mount = this._marker(MARKERS.loginMount);
    mount?.replaceChildren(createAccountMenuLoginView(flow, {
      accountUrls: accountFlowUrls(
        globalThis.window?.location?.origin,
        new URL(accountUrl(globalThis.window?.location)).pathname,
      ),
    }));
    this._renderMode("login");
  }

  async _startLogin(signal) {
    if (this._loginFlow) {
      this._renderMode("login");
      return;
    }
    this._renderMode("loading");
    const outcome = await this._client.startFlow("login", { signal });
    if (outcome.kind === "sessionAlreadyAvailable") {
      const state = await this._refreshRepository();
      if (!this._connected || signal.aborted) return;
      if (state.kind !== "ready") throw new Error("Identity session could not be loaded.");
      this._closeMenu();
      return;
    }
    if (outcome.kind !== "flow") throw new Error("Identity login is unavailable.");
    if (this._connected && !signal.aborted) this._renderLoginFlow(outcome.flow);
  }

  async _submitLogin(form, submitter, signal) {
    if (!this._loginFlow || form.action !== this._loginFlow.action) return;
    const submission = identityFormSubmission(form, submitter);
    this._setFormPending(form, true);
    try {
      const outcome = await this._client.submitFlow(
        this._loginFlow,
        submission,
        { signal },
      );
      if (outcome.kind === "authenticated") {
        await this._refreshRepository();
        this._closeMenu();
      } else if (["flow", "validation"].includes(outcome.kind)) {
        this._renderLoginFlow(outcome.flow);
      } else if (outcome.kind === "expired") {
        this._loginFlow = null;
        await this._startLogin(signal);
      } else {
        throw new Error("Identity login could not be completed.");
      }
    } finally {
      if (form.isConnected) this._setFormPending(form, false);
    }
  }

  _setFormPending(form, pending) {
    form.toggleAttribute?.("aria-busy", pending);
    for (const control of form.querySelectorAll?.("button, input") ?? []) {
      if (pending) {
        control.dataset.accountPendingDisabled = String(control.disabled);
        control.disabled = true;
      } else if (Object.hasOwn(control.dataset, "accountPendingDisabled")) {
        control.disabled = control.dataset.accountPendingDisabled === "true";
        delete control.dataset.accountPendingDisabled;
      }
    }
  }

  async _performPasskey(button) {
    if (this._passkeyAbortController) return;
    const form = button.closest("form");
    if (!form) return;
    const controller = new AbortController();
    this._passkeyAbortController = controller;
    button.disabled = true;
    try {
      await this._passkeyCeremony(form, button.dataset.accountMenuPasskey, {
        signal: controller.signal,
      });
      if (this._connected && !controller.signal.aborted) form.requestSubmit();
    } catch (error) {
      if (error?.name !== "AbortError") {
        const copy = webauthnCeremonyFailureCopy(
          error,
          button.dataset.accountMenuPasskey,
        );
        this._toastError(translate(copy.key, copy.fallback));
      }
    } finally {
      if (this._passkeyAbortController === controller) {
        this._passkeyAbortController = null;
        if (button.isConnected) button.disabled = false;
      }
    }
  }

  async _startDiscord(signal) {
    const outcome = await this._providerPopup({
      view: "login",
      apiBaseUrl: globalThis.window?.__fishystuffApiBaseUrl,
      locationLike: globalThis.window?.location,
      signal,
    });
    if (outcome.kind !== "returned") {
      const copy = identityProviderOutcomePresentation(outcome.kind);
      this._toastError(translate(copy.key, copy.fallback));
      return;
    }
    const state = await this._refreshRepository();
    if (state.kind !== "ready") {
      const copy = identityProviderOutcomePresentation("sessionUnavailable");
      this._toastError(translate(copy.key, copy.fallback));
      return;
    }
    this._closeMenu();
  }

  async _signOut(signal) {
    const logout = await this._client.createLogout({ signal });
    if (logout.kind === "ready") {
      await this._client.logout(logout.logoutUrl, { signal });
    }
    await this._refreshRepository();
    this._closeMenu();
  }

  _closeMenu() {
    const details = this.querySelector("details");
    if (details) details.open = false;
  }

  _onSubmit(event) {
    const form = event.target?.closest?.("[data-account-menu-identity-form]");
    if (!form || !this.contains(form)) return;
    event.preventDefault();
    if (this._actionAbortController) return;
    void this._runAction((signal) => this._submitLogin(form, event.submitter, signal));
  }

  _onClick(event) {
    const login = event.target?.closest?.(MARKERS.login);
    if (login && this.contains(login)) {
      event.preventDefault();
      if (!this._actionAbortController) {
        void this._runAction((signal) => this._startLogin(signal));
      }
      return;
    }
    const back = event.target?.closest?.("[data-account-menu-login-back]");
    if (back && this.contains(back)) {
      event.preventDefault();
      this._renderMode("signedOut");
      return;
    }
    const passkey = event.target?.closest?.("[data-account-menu-passkey]");
    if (passkey && this.contains(passkey)) {
      event.preventDefault();
      void this._performPasskey(passkey);
      return;
    }
    const discord = event.target?.closest?.(MARKERS.discord);
    if (discord && this.contains(discord)) {
      event.preventDefault();
      if (!this._actionAbortController) {
        void this._runAction((signal) => this._startDiscord(signal));
      }
      return;
    }
    const signOut = event.target?.closest?.(
      `${MARKERS.signOut}, ${MARKERS.recoverySignOut}`,
    );
    if (signOut && this.contains(signOut)) {
      event.preventDefault();
      if (!this._actionAbortController) {
        void this._runAction((signal) => this._signOut(signal));
      }
    }
  }
}

export function registerAccountMenu() {
  if (
    typeof globalThis.customElements === "undefined"
    || globalThis.customElements.get(ACCOUNT_MENU_TAG)
  ) return;
  globalThis.customElements.define(ACCOUNT_MENU_TAG, FishyAccountMenu);
}
